import { Link } from '@tanstack/react-router';
import { isLeadsRole, today } from '@cis/shared';
import type { TaskDetail } from '@cis/api-client';
import { useMe } from '../../api/me.ts';
import { useTaskDetail } from '../../api/tasks.ts';
import { can } from '../../shell/nav.ts';
import { AsOfBar } from '../../ui/as-of-bar.tsx';
import { ErrorPanel } from '../../ui/error-boundary.tsx';
import { Skeleton } from '../../ui/skeleton.tsx';
import { useFreezeParam } from '../freeze/index.ts';
import { ConflictDialog } from './conflict-dialog.tsx';
import { formatDate } from './format.ts';
import { FrozenTaskPage } from './frozen-task-page.tsx';
import { HistoryPanel } from './history-panel.tsx';
import { PlanPanel } from './plan-panel.tsx';
import { Runbook } from './runbook.tsx';
import { StatusPill } from './status-pill.tsx';
import { TaskActions } from './task-actions.tsx';
import { CommentsPanelSlot, FreezeBannerSlot } from './task-slots.tsx';
import { TaskUpdate } from './task-update.tsx';
import { useTaskWriter } from './use-task-writer.ts';
import '../../styles/ui.css';

const SKELETON_LINES = 10;

// Task id as the API accepts it: matches "T-OPS-026"; rejects "T-OPS-26", "../admin", "t-ops-026".
// https://regex101.com/?flavor=javascript&regex=%5ET-%5BA-Z%5D%7B3%7D-%5Cd%7B3%7D%24&testString=T-OPS-026%0AT-OPS-26%0A..%2Fadmin
const TASK_ID = /^T-[A-Z]{3}-\d{3}$/;

export function isTaskId(value: string): boolean {
  return TASK_ID.test(value);
}

function NoSuchTask(): React.JSX.Element {
  return (
    <section className="page">
      <h1 className="page__title">No such task</h1>
      <p className="page__hint">
        This address does not name a task. <Link to="/tasks">Go to the task list</Link>.
      </p>
    </section>
  );
}

function TaskFacts(props: { readonly detail: TaskDetail }): React.JSX.Element {
  const d = props.detail;
  const people = d.assignees.map((p) => p.displayName).join(', ');
  return (
    <dl className="facts">
      <dt>Status</dt>
      <dd>
        <StatusPill status={d.state.status} />
        {d.overdue && <span className="pill pill--overdue">Overdue</span>}
      </dd>
      <dt>Phase</dt>
      <dd>{d.phase.code}</dd>
      <dt>Area</dt>
      <dd>{d.area}</dd>
      <dt>Due</dt>
      <dd>{formatDate(d.due)}</dd>
      <dt>Assigned to</dt>
      <dd>{people === '' ? 'Nobody yet' : people}</dd>
      <dt>Done</dt>
      <dd>{d.state.percent}%</dd>
      {d.state.blockedReason !== null && d.state.status === 'blocked' && (
        <>
          <dt>Blocked because</dt>
          <dd>{d.state.blockedReason}</dd>
        </>
      )}
      {d.state.deferredReason !== null && d.state.status === 'deferred' && (
        <>
          <dt>Deferred because</dt>
          <dd>{d.state.deferredReason}</dd>
        </>
      )}
    </dl>
  );
}

function TaskLink(props: { readonly taskId: string }): React.JSX.Element {
  return (
    <Link to="/tasks/$taskId" params={{ taskId: props.taskId }} search={{}}>
      {props.taskId}
    </Link>
  );
}

function Dependencies(props: { readonly detail: TaskDetail }): React.JSX.Element | null {
  const deps = props.detail.dependencies;
  if (deps.length === 0) return null;
  return (
    <section className="card" aria-labelledby="deps-title">
      <h2 className="card__title" id="deps-title">
        Depends on
      </h2>
      <ul className="plain-list">
        {deps.map((d) => (
          <li key={d.taskId}>
            <TaskLink taskId={d.taskId} /> {d.title}
            {d.retired && d.successorTaskId !== null && (
              <span className="muted">
                {' '}
                (retired: see <TaskLink taskId={d.successorTaskId} />)
              </span>
            )}
            {d.retired && d.successorTaskId === null && <span className="muted"> (retired)</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function TaskBody(props: {
  readonly detail: TaskDetail;
  readonly asOf: string | undefined;
  readonly refreshing: boolean;
}): React.JSX.Element {
  const { detail, asOf } = props;
  const me = useMe();
  const writer = useTaskWriter(detail);
  const readOnly = asOf !== undefined;
  const successor = detail.retired?.successorTaskId ?? null;
  return (
    <>
      <TaskFacts detail={detail} />
      {detail.retired !== null && (
        <p className="card card--alert" role="note">
          This task is retired.{' '}
          {successor === null ? (
            'Nothing replaces it.'
          ) : (
            <>
              See <TaskLink taskId={successor} /> instead.
            </>
          )}
        </p>
      )}
      {!readOnly && <TaskActions actions={detail.permissions.actions} writer={writer} />}
      {!readOnly && <PlanPanel detail={detail} writer={writer} />}
      <TaskUpdate
        detail={detail}
        writer={writer}
        leadsReader={isLeadsRole(me.user.role)}
        readOnly={readOnly}
      />
      <Runbook detail={detail} writer={writer} readOnly={readOnly} />
      <Dependencies detail={detail} />
      {!readOnly && (
        <ConflictDialog writer={writer} current={detail} refreshing={props.refreshing} />
      )}
    </>
  );
}

interface TaskPageProps {
  readonly taskId: string;
  readonly asOf: string | undefined;
  readonly onAsOf: (asOf: string | undefined) => void;
}

/** Task page (T21, spec F4): runbook with ticks, actions, 409 handling, history, as-of view. */
export function TaskPage(props: TaskPageProps): React.JSX.Element {
  return isTaskId(props.taskId) ? <LiveOrFrozenTaskPage {...props} /> : <NoSuchTask />;
}

// `?freeze=<id>` (T32) shows the frozen record instead of the live page.
function LiveOrFrozenTaskPage(props: TaskPageProps): React.JSX.Element {
  const { freezeId } = useFreezeParam();
  return freezeId === null ? (
    <LoadedTaskPage {...props} />
  ) : (
    <FrozenTaskPage taskId={props.taskId} freezeId={freezeId} />
  );
}

function LoadedTaskPage(props: TaskPageProps): React.JSX.Element {
  const { taskId, asOf } = props;
  const me = useMe();
  const detail = useTaskDetail(taskId, asOf);
  const slot = { taskId, asOf };
  return (
    <section className="page" aria-labelledby="task-title">
      {/* MERGE SLOT: lane C FreezeBanner (see task-slots.tsx). */}
      <FreezeBannerSlot {...slot} />
      <div className="page__head">
        <h1 className="page__title" id="task-title">
          <span className="mono">{taskId}</span> {detail.data?.title ?? ''}
        </h1>
        <Link to="/tasks" className="muted">
          Back to tasks
        </Link>
      </div>
      {can(me, 'history.read') && (
        <AsOfBar key={asOf ?? 'live'} asOf={asOf} max={today()} onChange={props.onAsOf} />
      )}
      {detail.isPending && <Skeleton lines={SKELETON_LINES} label="Loading the task…" />}
      {detail.isError && (
        <ErrorPanel
          title="This task could not be shown"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      )}
      {detail.data !== undefined && (
        <TaskBody detail={detail.data} asOf={asOf} refreshing={detail.isFetching} />
      )}
      {/* MERGE SLOT: lane B CommentsPanel (see task-slots.tsx). */}
      <CommentsPanelSlot {...slot} />
      {detail.data !== undefined && <HistoryPanel taskId={taskId} />}
    </section>
  );
}
